
import React, { useState } from 'react';
import StationSelector from './StationSelector';
import MarketOverview from './MarketOverview';
import CompetitorAnalysis from './CompetitorAnalysis';
import CustomerFlow from './CustomerFlow';
import PriceElasticity from './PriceElasticity';
import StationDetailedAnalysis from './StationDetailedAnalysis';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'react-hot-toast';

const TradeAreaAnalyzerView = () => {
  // Default station with required coordinates to prevent undefined errors
  const defaultStation = {
    id: 'BKK001',
    name: 'Sukhumvit 71',
    area: 'Bangkok - East',
    format: 'Urban Premium',
    volume: 12500,
    margin: 0.89,
    competitors: 3,
    lat: 13.7307,
    lng: 100.5847,
  };

  const [selectedStation, setSelectedStation] = useState(defaultStation);
  const [activeTab, setActiveTab] = useState('overview');
  const [showDetailedAnalysis, setShowDetailedAnalysis] = useState(false);
  const [previousTab, setPreviousTab] = useState('overview');

  const handleSelectStation = (station) => {
    if (!station) return; // Guard against undefined stations
    setSelectedStation(station);
    setShowDetailedAnalysis(false); // Reset detailed view when station changes
  };

  const handleViewDetailed = () => {
    setPreviousTab(activeTab);
    setShowDetailedAnalysis(true);
  };

  const handleCloseDetailed = () => {
    setShowDetailedAnalysis(false);
    if (previousTab) {
      setActiveTab(previousTab);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setShowDetailedAnalysis(false);
  };

  return (
    <div className="flex flex-col bg-gray-50">
      {/* Station Selection */}
      <StationSelector 
        onSelectStation={handleSelectStation}
        selectedStation={selectedStation}
      />

      {/* Trade Area analysis */}
      <div className="bg-white rounded-lg shadow mt-6">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-start">
            <div>
              <h2 className="text-xl font-medium text-gray-900 mb-1">
                {selectedStation ? selectedStation.name : 'Select a Station'} Trade Area Analysis
              </h2>
              <p className="text-gray-500">
                {selectedStation ? (
                  `ID: ${selectedStation.id} • Format: ${selectedStation.format} • ${selectedStation.area}`
                ) : 'Please select a station to begin analysis'}
              </p>
            </div>
            <div className="flex space-x-3">
              <button className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
                Export Data
              </button>
              <button className="px-4 py-2 bg-purple-700 text-white rounded-md hover:bg-purple-800">
                View Full Map
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="px-6">
            <div className="flex space-x-8">
              <button
                onClick={() => handleTabChange('overview')}
                className={`py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'overview' && !showDetailedAnalysis
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Market Overview
              </button>
              <button
                onClick={() => handleTabChange('competitors')}
                className={`py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'competitors' && !showDetailedAnalysis
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Competitor Analysis
              </button>
              <button
                onClick={() => handleTabChange('customers')}
                className={`py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'customers' && !showDetailedAnalysis
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Customer Flow
              </button>
              <button
                onClick={() => handleTabChange('elasticity')}
                className={`py-4 text-sm font-medium border-b-2 ${
                  activeTab === 'elasticity' && !showDetailedAnalysis
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Price Elasticity
              </button>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {/* Show detailed analysis when requested */}
          {showDetailedAnalysis && selectedStation ? (
            <div>
              {/* Back Button */}
              <button 
                onClick={handleCloseDetailed}
                className="flex items-center mb-6 px-3 py-2 bg-white border border-gray-300 rounded-md text-purple-700 hover:text-purple-900 hover:bg-gray-50 font-medium shadow-sm transition-colors"
              >
                <ArrowLeft size={16} className="mr-2" />
                Back to {activeTab === 'overview' ? 'Market Overview' : 
                        activeTab === 'competitors' ? 'Competitor Analysis' :
                        activeTab === 'customers' ? 'Customer Flow' : 'Price Elasticity'}
              </button>
              
              <StationDetailedAnalysis 
                station={selectedStation} 
                onClose={handleCloseDetailed} 
              />
            </div>
          ) : (
            <>
              {activeTab === 'overview' && selectedStation && (
                <MarketOverview station={selectedStation} onViewDetailed={handleViewDetailed} />
              )}
              
              {activeTab === 'competitors' && selectedStation && (
                <CompetitorAnalysis station={selectedStation} />
              )}
              
              {activeTab === 'customers' && selectedStation && (
                <CustomerFlow station={selectedStation} />
              )}
              
              {activeTab === 'elasticity' && selectedStation && (
                <PriceElasticity station={selectedStation} />
              )}
            </>
          )}
          
          {!selectedStation && (
            <div className="text-center py-12">
              <p className="text-gray-500">Please select a station to view analysis</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TradeAreaAnalyzerView;
